<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper"
        :style="{ paddingLeft: normalizeSize(55), gap: normalizeSize(30) }"
    >
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- Mise en ligne et affichage Section -->
            <DashedCard
                :marginTop="0"
                :marginBottom="30"
                :custom-style="{ padding: normalizeSize(24, true), justifyContent: 'center' }"
            >
                <template #title>
                    <span class="poppinsLight ppv2-text-black">{{ $t('treatment.step5.visible_section') }}</span>
                </template>
                <template #content>
                    <div class="ppv2-space-y-4">
                        <!-- Visibility Toggle -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                            <BaseSwitch
                                id="visible_online"
                                v-model="options.visible_online"
                                :label="$t('treatment.step5.visible_label')"
                                :label-left="null"
                                @focus="activeField = 'visibility'"
                                @blur="activeField = null"
                            />
                        </div>
                    </div>
                </template>
            </DashedCard>

            <!-- Options de billetterie Section -->
            <DashedCard
                :marginTop="0"
                :marginBottom="30"
                :custom-style="{ padding: normalizeSize(24, true), justifyContent: 'center' }"
            >
                <template #title>
                    <span class="poppinsLight ppv2-text-black">{{ $t('treatment.step5.ticketing_section') }}</span>
                </template>
                <template #content>
                    <div class="ppv2-space-y-4">
                        <!-- Undated Ticket Toggle -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                            <BaseSwitch
                                id="as_undated_ticket"
                                v-model="options.as_undated_ticket"
                                :label="$t('treatment.step5.undated_label')"
                                :label-left="null"
                                @focus="activeField = 'undated'"
                                @blur="activeField = null"
                            />
                        </div>
                    </div>
                </template>
            </DashedCard>

            <!-- Action Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': complited }">
                <BackButton v-show="!complited" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="button" @click="saveAndFinish" variant="primary" :loading="loading">
                    {{ complited ? $t('common.save') : $t('experience.finish') }}
                </BaseButton>
            </div>
        </div>
        <PreviewTreatment :active-field="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import PreviewTreatment from './PreviewTreatment.vue'
import HeaderStep from './HeaderStep.vue'
import normalizeSize from '../../../../utils/normalizeSize'

export default {
    name: 'TreatmentStep5',
    components: {
        BaseButton,
        BaseSwitch,
        DashedCard,
        BackButton,
        PreviewTreatment,
        HeaderStep,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        complited: { type: Boolean, default: false },
    },
    data() {
        return {
            activeField: null,
            initialDataString: null,
        }
    },
    computed: {
        ...mapGetters('treatment', ['options', 'isEditMode', 'loading']),
        options: {
            get() {
                return this.$store.getters['treatment/options']
            },
            set(value) {
                this.$store.dispatch('treatment/setOptions', value)
            },
        },
    },
    mounted() {
        this.initialDataString = this.getFormDataString()
    },

    methods: {
        ...mapActions('treatment', ['saveTreatment', 'resetStore']),
        normalizeSize,

        getFormDataString() {
            return JSON.stringify({
                visible_online: this.options.visible_online,
                as_undated_ticket: this.options.as_undated_ticket,
            })
        },

        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },

        revertChanges() {
            if (!this.initialDataString) return
            const data = JSON.parse(this.initialDataString)
            this.$store.dispatch('treatment/setOptions', {
                visible_online: data.visible_online,
                as_undated_ticket: data.as_undated_ticket,
            })
        },

        goToList() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },

        async saveAndFinish() {
            // Save to API with error handling
            try {
                await this.saveTreatment()

                // Show success message
                this.$toast?.success?.(this.$t('treatment.step5.success_save'))

                // Redirect to list only if save was successful
                this.goToList()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                // Don't redirect if there was an error
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}
</style>
